import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listNotes, fetchCategories } from "@/lib/api";
import CategoryChips from "@/components/CategoryChips";
import JournalNotesTabs from "@/components/JournalNotesTabs";
import { Plus, Search } from "lucide-react";

export default function NotesPage() {
    const [notes, setNotes] = useState([]);
    const [cats, setCats] = useState([]);
    const [cat, setCat] = useState("__all__");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    const load = () => {
        setLoading(true);
        const params = {};
        if (cat && cat !== "__all__") params.category = cat;
        if (q.trim()) params.q = q.trim();
        listNotes(params).then(d => setNotes(d.notes || [])).finally(() => setLoading(false));
    };
    useEffect(() => {
        const t = setTimeout(load, q ? 200 : 0);
        return () => clearTimeout(t);
    }, [cat, q]); // eslint-disable-line
    useEffect(() => { fetchCategories("notes").then(d => setCats(d.categories || [])); }, []);

    return (
        <div className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full" style={{ maxWidth: "68ch" }}>
            <JournalNotesTabs current="notes" />
            <div className="flex items-center justify-between mb-4">
                <h1 className="scripture text-3xl md:text-4xl text-ink tracking-tight">Notes</h1>
                <button
                    data-testid="new-note"
                    onClick={() => nav("/notes/new")}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-moss text-white font-ui text-[13px] font-medium hover:bg-moss-deep transition"
                >
                    <Plus size={16} /> New note
                </button>
            </div>

            <div className="relative mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-40" />
                <input
                    data-testid="notes-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search title, tag, text…"
                    className="w-full pl-9 pr-3 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] outline-none focus:border-moss focus:ring-2 focus:ring-moss/20"
                />
            </div>

            <CategoryChips
                space="notes"
                categories={cats}
                selectedId={cat}
                onSelect={setCat}
                variant="filter"
            />

            {loading ? (
                <p className="mt-6 font-ui text-sm text-ink-40">Loading…</p>
            ) : notes.length === 0 ? (
                <div className="mt-10 text-center">
                    <p className="scripture text-[18px] text-ink-70">A blank page, and time.</p>
                    <p className="mt-1 font-ui text-[13px] text-ink-40">Your notes will collect here.</p>
                </div>
            ) : (
                <ul className="mt-4 space-y-3">
                    {notes.map(n => (
                        <li key={n.id}>
                            <Link
                                to={`/notes/${n.id}`}
                                data-testid={`note-item-${n.id}`}
                                className="block p-4 rounded-2xl bg-white border border-ink-10 hover:border-moss/40 hover:shadow-md transition"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <div className="scripture text-[16px] text-ink font-medium">
                                        {n.title || <span className="text-ink-40 italic">Untitled</span>}
                                    </div>
                                    {n.category_id && (
                                        <CategoryBadge cats={cats} id={n.category_id} />
                                    )}
                                </div>
                                {n.tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 my-1">
                                        {n.tags.map(t => (
                                            <span key={t} className="px-1.5 py-0.5 rounded bg-sage-paper text-ink-70 font-ui text-[10px]">#{t}</span>
                                        ))}
                                    </div>
                                )}
                                <p className="scripture text-[14px] text-ink-70 leading-snug line-clamp-2">
                                    {n.excerpt || <span className="text-ink-40 italic">Empty note</span>}
                                </p>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function CategoryBadge({ cats, id }) {
    const c = cats.find(x => x.id === id);
    if (!c) return null;
    return <span className="px-2 py-0.5 rounded-full font-ui text-[10px] font-medium text-white" style={{ background: c.color }}>{c.name}</span>;
}
